import { create } from 'zustand';
import { baseUrl, getRequest } from '../utils/services';
import {
  areChatsEqual,
  isSameTicket,
  mergeTicketMetadata,
  removeChatFromList,
  sortUserChatsByRecentMessages,
  upsertChatInList,
  updateTicketMetadataList,
} from '../utils/chatState';

const MAX_QUEUE_SNAPSHOT_RETRIES = 2;
const QUEUE_SNAPSHOT_RETRY_DELAY_MS = 250;
const MAX_QUEUE_SNAPSHOT_RETRY_DELAY_MS = 4000;
let scheduledQueueSnapshotRetry = null;

const cancelScheduledQueueSnapshotRetry = () => {
  if (scheduledQueueSnapshotRetry !== null) {
    clearTimeout(scheduledQueueSnapshotRetry.timerId);
    scheduledQueueSnapshotRetry = null;
  }
};

const postponeScheduledQueueSnapshotRetry = () => {
  if (scheduledQueueSnapshotRetry === null) return;

  clearTimeout(scheduledQueueSnapshotRetry.timerId);
  scheduledQueueSnapshotRetry.timerId = setTimeout(
    scheduledQueueSnapshotRetry.run,
    scheduledQueueSnapshotRetry.delay,
  );
};

const scheduleQueueSnapshotRetry = (
  get,
  user,
  setErrorMessage,
  deferredRetryAttempt,
) => {
  cancelScheduledQueueSnapshotRetry();

  const delay = Math.min(
    QUEUE_SNAPSHOT_RETRY_DELAY_MS * (2 ** Math.max(0, deferredRetryAttempt - 1)),
    MAX_QUEUE_SNAPSHOT_RETRY_DELAY_MS,
  );
  const run = () => {
    scheduledQueueSnapshotRetry = null;
    get().getUserChatsQueue(user, setErrorMessage, {
      deferredRetryAttempt,
      mutationRetryCount: MAX_QUEUE_SNAPSHOT_RETRIES,
      replace: true,
    });
  };

  scheduledQueueSnapshotRetry = {
    delay,
    run,
    timerId: setTimeout(run, delay),
  };
};

export const useTicketStore = create((set, get) => ({
  // Chat/Ticket listings
  currentChat: null,
  userChats: [],
  sortedChats: [],
  userChatsQueue: [],
  totalUserChatsQueue: 0,
  queueNextCursor: null,
  hasMoreQueue: true,
  waitingQueueGeneration: 0,
  waitingQueueMutationRevision: 0,
  openTickets: [],
  totalOpenTickets: 0,

  // Internal chats
  internalChats: [],
  archivedInternalChats: [],
  openArchivedInternalChats: false,
  unreadInternalMessagesCount: 0,

  // Departments and users
  departments: [],
  allUsers: [],

  // Filters
  selectedDepartment: 'default',
  selectedAttendant: 'default',
  selectedSenderKey: '',

  // Transfer
  isTicketTransferred: false,
  openTransferModal: false,

  // Loading states
  isUserChatsLoading: false,
  isOpenTicketsLoading: false,
  isInternalChatsLoading: false,

  // Tags
  chatTags: {
    currentContactId: null,
    associatedTags: [],
    availableTags: [],
    newTag: '',
  },

  // Kanban
  kanban: {
    kanbanBoards: [],
    currentKanban: {},
    currentChatKanbans: [],
    showKanbanSection: false,
    showAddNewBoard: false,
    isLoadingBoard: false,
  },

  // Setters
  setCurrentChat: (chatOrFn) => set((state) => ({
    currentChat: typeof chatOrFn === 'function' ? chatOrFn(state.currentChat) : chatOrFn,
  })),
  setUserChats: (chatsOrFn) => set((state) => ({
    userChats: typeof chatsOrFn === 'function' ? chatsOrFn(state.userChats) : chatsOrFn,
  })),
  setSortedChats: (chatsOrFn) => set((state) => ({
    sortedChats: typeof chatsOrFn === 'function' ? chatsOrFn(state.sortedChats) : chatsOrFn,
  })),
  setUserChatsQueue: (queueOrFn) => {
    postponeScheduledQueueSnapshotRetry();
    set((state) => ({
      userChatsQueue: typeof queueOrFn === 'function' ? queueOrFn(state.userChatsQueue) : queueOrFn,
      waitingQueueMutationRevision: state.waitingQueueMutationRevision + 1,
    }));
  },
  syncWaitingQueueTicket: (ticket, canViewInWaitingQueue) => {
    postponeScheduledQueueSnapshotRetry();
    set((state) => {
    const matchesTicket = (queuedTicket) => (
      areChatsEqual(queuedTicket, ticket)
      || (
        queuedTicket?.id != null
        && ticket?.id != null
        && Number(queuedTicket.id) === Number(ticket.id)
      )
    );
    const existingIndex = state.userChatsQueue.findIndex(matchesTicket);
    const shouldBeInQueue = Number(ticket?.status) === 1
      && canViewInWaitingQueue === true;

    if (!shouldBeInQueue) {
      if (existingIndex === -1) {
        return {
          waitingQueueMutationRevision: state.waitingQueueMutationRevision + 1,
        };
      }

      return {
        userChatsQueue: state.userChatsQueue.filter((_, index) => index !== existingIndex),
        totalUserChatsQueue: Math.max(0, state.totalUserChatsQueue - 1),
        waitingQueueMutationRevision: state.waitingQueueMutationRevision + 1,
      };
    }

    if (existingIndex !== -1) {
      return {
        userChatsQueue: sortUserChatsByRecentMessages(
          state.userChatsQueue.map((queuedTicket, index) => (
            index === existingIndex
              ? { ...queuedTicket, ...ticket }
              : queuedTicket
          )),
        ),
        waitingQueueMutationRevision: state.waitingQueueMutationRevision + 1,
      };
    }

    return {
      userChatsQueue: sortUserChatsByRecentMessages([ticket, ...state.userChatsQueue]),
      totalUserChatsQueue: state.totalUserChatsQueue + 1,
      waitingQueueMutationRevision: state.waitingQueueMutationRevision + 1,
      };
    });
  },
  setTotalUserChatsQueue: (totalOrFn) => set((state) => ({
    totalUserChatsQueue: typeof totalOrFn === 'function' ? totalOrFn(state.totalUserChatsQueue) : totalOrFn,
  })),
  setOpenTickets: (ticketsOrFn) => set((state) => ({
    openTickets: typeof ticketsOrFn === 'function' ? ticketsOrFn(state.openTickets) : ticketsOrFn,
  })),
  setTotalOpenTickets: (totalOrFn) => set((state) => ({
    totalOpenTickets: typeof totalOrFn === 'function' ? totalOrFn(state.totalOpenTickets) : totalOrFn,
  })),
  setInternalChats: (chatsOrFn) => set((state) => ({
    internalChats: typeof chatsOrFn === 'function'
      ? chatsOrFn(state.internalChats)
      : chatsOrFn,
  })),
  setArchivedInternalChats: (chatsOrFn) => set((state) => ({
    archivedInternalChats: typeof chatsOrFn === 'function' ? chatsOrFn(state.archivedInternalChats) : chatsOrFn,
  })),
  setUnreadInternalMessagesCount: (count) => set({ unreadInternalMessagesCount: count }),
  setDepartments: (departments) => set({ departments }),
  setAllUsers: (users) => set({ allUsers: users }),
  setSelectedDepartment: (department) => set({ selectedDepartment: department }),
  setSelectedAttendant: (attendant) => set({ selectedAttendant: attendant }),
  setSelectedSenderKey: (key) => set({ selectedSenderKey: key }),
  setIsTicketTransferred: (transferred) => set({ isTicketTransferred: transferred }),
  setOpenTransferModal: (open) => set({ openTransferModal: open }),
  setIsUserChatsLoading: (loading) => set({ isUserChatsLoading: loading }),
  setIsOpenTicketsLoading: (loading) => set({ isOpenTicketsLoading: loading }),
  setIsInternalChatsLoading: (loading) => set({ isInternalChatsLoading: loading }),
  setChatTags: (tagsOrFn) => set((state) => ({
    chatTags: typeof tagsOrFn === 'function' ? tagsOrFn(state.chatTags) : tagsOrFn,
  })),
  setKanban: (kanbanOrFn) => set((state) => ({
    kanban: typeof kanbanOrFn === 'function' ? kanbanOrFn(state.kanban) : kanbanOrFn,
  })),
  setOpenArchivedInternalChats: (open) => set({
    openArchivedInternalChats: open,
  }),
  upsertGeneralChat: (chat, currentChat = null) => set((state) => ({
    userChats: upsertChatInList(state.userChats, chat, currentChat),
    sortedChats: upsertChatInList(state.sortedChats, chat, currentChat),
  })),
  resetWaitingQueue: ({ preserveItems = false } = {}) => {
    cancelScheduledQueueSnapshotRetry();
    set((state) => ({
      waitingQueueGeneration: state.waitingQueueGeneration + 1,
      isUserChatsLoading: false,
      ...(!preserveItems && {
        userChatsQueue: [],
        totalUserChatsQueue: 0,
        queueNextCursor: null,
        hasMoreQueue: true,
      }),
    }));
  },
  promoteWaitingTicket: (ticket) => {
    postponeScheduledQueueSnapshotRetry();
    set((state) => {
    const matchesTicket = (item) => (
      areChatsEqual(item, ticket)
      || (
        item?.id != null
        && ticket?.id != null
        && Number(item.id) === Number(ticket.id)
      )
    );
    const queueHadTicket = state.userChatsQueue.some(matchesTicket);
    const openTicketsHadTicket = state.openTickets.some(matchesTicket);
    const nextQueue = state.userChatsQueue.filter((item) => !matchesTicket(item));
    const nextOpenTickets = sortUserChatsByRecentMessages([
      ticket,
      ...state.openTickets.filter((item) => !matchesTicket(item)),
    ]);

    return {
      currentChat: ticket,
      userChats: upsertChatInList(state.userChats, ticket, ticket),
      sortedChats: upsertChatInList(state.sortedChats, ticket, ticket),
      userChatsQueue: nextQueue,
      totalUserChatsQueue: Math.max(
        0,
        state.totalUserChatsQueue - (queueHadTicket ? 1 : 0),
      ),
      openTickets: nextOpenTickets,
      totalOpenTickets: state.totalOpenTickets + (openTicketsHadTicket ? 0 : 1),
      waitingQueueMutationRevision: state.waitingQueueMutationRevision + 1,
      };
    });
  },
  syncTransferredOpenTicket: ({
    previousTicket,
    notification,
    currentUserId,
  } = {}) => {
    set((state) => {
      if (!notification?.sender || !notification?.fromMsg) {
        return {};
      }

      const matchesTicket = (ticket) => isSameTicket(ticket, notification);
      const existingIndex = state.openTickets.findIndex(matchesTicket);
      const wasOwnedByCurrentUser = Number(previousTicket?.status) === 2
        && Number(previousTicket?.usuario_id) === Number(currentUserId);
      const isOwnedByCurrentUser = Number(notification.status) === 2
        && Number(notification.usuario_id) === Number(currentUserId);

      if (isOwnedByCurrentUser) {
        if (existingIndex === -1 && wasOwnedByCurrentUser) {
          return {};
        }

        if (existingIndex !== -1) {
          const existingTicket = state.openTickets[existingIndex];
          const shouldIncrement = existingTicket.shouldIncrementOpenTicketsOnSocket === true;
          const hasConsumedIncrementMarker = (
            existingTicket.shouldIncrementOpenTicketsOnSocket === false
          );
          const updatedTicket = {
            ...existingTicket,
            ...notification,
          };

          if (shouldIncrement || hasConsumedIncrementMarker) {
            updatedTicket.shouldIncrementOpenTicketsOnSocket = false;
          }

          const updatedTickets = state.openTickets.map((ticket, index) => (
            index === existingIndex
              ? updatedTicket
              : ticket
          ));

          return {
            openTickets: sortUserChatsByRecentMessages(updatedTickets),
            ...(shouldIncrement && {
              totalOpenTickets: state.totalOpenTickets + 1,
            }),
          };
        }

        return {
          openTickets: sortUserChatsByRecentMessages([
            {
              ...notification,
              shouldIncrementOpenTicketsOnSocket: false,
            },
            ...state.openTickets,
          ]),
          totalOpenTickets: state.totalOpenTickets + 1,
        };
      }

      if (wasOwnedByCurrentUser && existingIndex !== -1) {
        return {
          openTickets: state.openTickets.filter((_, index) => index !== existingIndex),
          totalOpenTickets: Math.max(0, state.totalOpenTickets - 1),
        };
      }

      return {};
    });
  },
  updateTicketMetadata: (ticket, userId) => {
    postponeScheduledQueueSnapshotRetry();
    set((state) => {
    const hasStatus = ticket?.status !== undefined && ticket?.status !== null;
    const matchesTicket = (chat) => (
      areChatsEqual(chat, ticket)
      || (chat?.id != null && ticket?.id != null && Number(chat.id) === Number(ticket.id))
    );
    const keepsQueueTicket = (chat) => (
      !matchesTicket(chat)
      || !hasStatus
      || (
        Number(ticket.status) === 1
        && ticket?.canViewInWaitingQueue !== false
      )
    );
    const keepsOpenTicket = (chat) => (
      !matchesTicket(chat)
      || !hasStatus
      || (Number(ticket.status) === 2 && Number(ticket.usuario_id) === Number(userId))
    );
    const nextUserChatsQueue = state.userChatsQueue
      .filter(keepsQueueTicket);
    const nextOpenTickets = state.openTickets
      .filter(keepsOpenTicket);

    return {
      currentChat: mergeTicketMetadata(state.currentChat, ticket),
      userChats: updateTicketMetadataList(state.userChats, ticket),
      sortedChats: updateTicketMetadataList(state.sortedChats, ticket),
      userChatsQueue: updateTicketMetadataList(nextUserChatsQueue, ticket),
      totalUserChatsQueue: Math.max(
        0,
        state.totalUserChatsQueue - (state.userChatsQueue.length - nextUserChatsQueue.length),
      ),
      openTickets: updateTicketMetadataList(nextOpenTickets, ticket),
      totalOpenTickets: Math.max(
        0,
        state.totalOpenTickets - (state.openTickets.length - nextOpenTickets.length),
      ),
      ...(hasStatus && {
        waitingQueueMutationRevision: state.waitingQueueMutationRevision + 1,
      }),
      };
    });
  },
  removeChatFromCollections: (chat) => {
    postponeScheduledQueueSnapshotRetry();
    set((state) => {
    const nextQueue = removeChatFromList(state.userChatsQueue, chat);
    const nextOpenTickets = removeChatFromList(state.openTickets, chat);

    return {
      currentChat: areChatsEqual(state.currentChat, chat) ? null : state.currentChat,
      userChats: removeChatFromList(state.userChats, chat),
      sortedChats: removeChatFromList(state.sortedChats, chat),
      userChatsQueue: nextQueue,
      totalUserChatsQueue: Math.max(
        0,
        state.totalUserChatsQueue - (state.userChatsQueue.length - nextQueue.length),
      ),
      openTickets: nextOpenTickets,
      totalOpenTickets: Math.max(
        0,
        state.totalOpenTickets - (state.openTickets.length - nextOpenTickets.length),
      ),
      waitingQueueMutationRevision: state.waitingQueueMutationRevision + 1,
      };
    });
  },
  markChatNotificationsAsRead: (chat) => {
    postponeScheduledQueueSnapshotRetry();
    set((state) => {
    const updatedChat = {
      ...chat,
      unreadCount: 0,
    };

    const replaceIfMatch = (item) => (item.fromMsg === updatedChat.fromMsg
      && item.sender === updatedChat.sender
      ? updatedChat
      : item);

    return {
      sortedChats: state.sortedChats.map(replaceIfMatch),
      userChatsQueue: state.userChatsQueue.map(replaceIfMatch),
      openTickets: state.openTickets.map(replaceIfMatch),
      waitingQueueMutationRevision: state.waitingQueueMutationRevision + 1,
      };
    });
  },

  // ====== ASYNC ACTIONS ======
  getAllUsers: async (user, setErrorMessage) => {
    const response = await getRequest(
      `${baseUrl}/users/${user.id}/${user.ambiente}`,
    );
    if (response.error) {
      setErrorMessage(
        'Erro ao buscar todos usuários. '
        + 'Entre em contato com o suporte',
      );
      set({ allUsers: [] });
      return;
    }
    set({ allUsers: response });
  },

  getDepartments: async (user, setErrorMessage) => {
    const response = await getRequest(
      `${baseUrl}/chats/departments/${user?.ambiente}`,
    );
    if (response.error) {
      setErrorMessage(
        'Erro ao buscar todos ambientes. '
        + 'Entre em contato com o suporte',
      );
    }
    set({ departments: response });
  },

  getUserChats: async (user, page = 1) => {
    set({ isUserChatsLoading: true });
    const response = await getRequest(
      `${baseUrl}/chats/${user?.id}?page=${page}&limit=30`,
    );
    if (response.error) {
      set({ isUserChatsLoading: false });
      return [];
    }

    if (page === 1) {
      set({
        userChats: response,
        sortedChats: sortUserChatsByRecentMessages(response),
        isUserChatsLoading: false,
      });
      return response;
    }

    const { userChats } = get();
    const filtered = response.filter(
      (nc) => !userChats?.some(
        (ec) => ec.sender === nc.sender
          && ec.fromMsg === nc.fromMsg,
      ),
    );
    set({
      userChats: [...userChats, ...filtered],
      sortedChats: sortUserChatsByRecentMessages([...userChats, ...filtered]),
      isUserChatsLoading: false,
    });
    return response;
  },

  getUserChatsQueue: async (user, setErrorMessage, options = {}) => {
    const {
      cursor = null,
      deferredRetryAttempt = 0,
      mutationRetryCount = 0,
      replace = true,
    } = options;
    let requestGeneration;

    if (replace) {
      if (mutationRetryCount === 0 && deferredRetryAttempt === 0) {
        cancelScheduledQueueSnapshotRetry();
      }
      set((state) => ({
        waitingQueueGeneration: state.waitingQueueGeneration + 1,
        isUserChatsLoading: true,
      }));
      requestGeneration = get().waitingQueueGeneration;
    } else {
      requestGeneration = get().waitingQueueGeneration;
      set({ isUserChatsLoading: true });
    }
    const requestMutationRevision = get().waitingQueueMutationRevision;
    const cursorQuery = cursor ? `&cursor=${encodeURIComponent(cursor)}` : '';
    let response;

    try {
      response = await getRequest(
        `${baseUrl}/chats/chatQueue/${user?.id}`
        + `?limit=30${cursorQuery}`,
      );
    } catch (error) {
      if (requestGeneration !== get().waitingQueueGeneration) {
        return {
          data: [],
          stale: true,
          page: { nextCursor: null, hasMore: false },
        };
      }

      setErrorMessage(
        'Erro ao buscar fila de chamados! '
        + 'Entre em contato com o suporte.',
      );
      set({ isUserChatsLoading: false });
      return {
        data: [],
        error: true,
        page: { nextCursor: null, hasMore: false },
      };
    }
    if (requestGeneration !== get().waitingQueueGeneration) {
      return {
        data: [],
        stale: true,
        page: { nextCursor: null, hasMore: false },
      };
    }
    if (
      !response.error
      && replace
      && requestMutationRevision !== get().waitingQueueMutationRevision
    ) {
      if (mutationRetryCount < MAX_QUEUE_SNAPSHOT_RETRIES) {
        return get().getUserChatsQueue(user, setErrorMessage, {
          cursor: null,
          deferredRetryAttempt,
          mutationRetryCount: mutationRetryCount + 1,
          replace: true,
        });
      }

      set({ isUserChatsLoading: false });
      scheduleQueueSnapshotRetry(
        get,
        user,
        setErrorMessage,
        deferredRetryAttempt + 1,
      );
      return {
        data: [],
        stale: true,
        page: { nextCursor: null, hasMore: false },
      };
    }
    if (response.error) {
      setErrorMessage(
        'Erro ao buscar fila de chamados! '
        + 'Entre em contato com o suporte.',
      );
      set({ isUserChatsLoading: false });
      return {
        data: [],
        page: { nextCursor: null, hasMore: false },
      };
    }
    const chatsArray = response.data || response;
    const totalCount = response.totalCount !== undefined
      ? response.totalCount
      : chatsArray.length;
    const responsePage = response.page || {
      nextCursor: null,
      hasMore: chatsArray.length >= 30,
    };

    if (replace) {
      set({
        userChatsQueue: sortUserChatsByRecentMessages(chatsArray),
        totalUserChatsQueue: Number(totalCount),
        queueNextCursor: responsePage.nextCursor || null,
        hasMoreQueue: Boolean(responsePage.hasMore),
      });
    } else {
      const { userChatsQueue } = get();
      const filtered = chatsArray.filter(
        (nc) => !userChatsQueue?.some(
          (ec) => ec.sender === nc.sender
            && ec.fromMsg === nc.fromMsg,
        ),
      );
      set({
        userChatsQueue: sortUserChatsByRecentMessages([...userChatsQueue, ...filtered]),
        totalUserChatsQueue: Number(totalCount),
        queueNextCursor: responsePage.nextCursor || null,
        hasMoreQueue: Boolean(responsePage.hasMore),
      });
    }
    set({ isUserChatsLoading: false });
    return {
      data: chatsArray,
      totalCount: Number(totalCount),
      page: responsePage,
    };
  },

  getOpenTickets: async (user, setErrorMessage, page = 1) => {
    set({ isOpenTicketsLoading: true });
    const response = await getRequest(
      `${baseUrl}/chats/openedTickets/${user?.id}`
      + `?page=${page}&limit=30`,
    );
    if (response.error) {
      setErrorMessage(
        'Erro ao buscar chamados em aberto. '
        + 'Entre em contato com o suporte',
      );
      set({ isOpenTicketsLoading: false });
      return [];
    }
    const chatsArray = response.data || response;
    const totalCount = response.totalCount !== undefined
      ? response.totalCount
      : chatsArray.length;
    set({ totalOpenTickets: totalCount });

    if (page === 1) {
      set({ openTickets: chatsArray });
    } else {
      const { openTickets } = get();
      const filtered = chatsArray.filter(
        (nc) => !openTickets?.some(
          (ec) => ec.sender === nc.sender
            && ec.fromMsg === nc.fromMsg,
        ),
      );
      set({
        openTickets: [...openTickets, ...filtered],
      });
    }
    set({ isOpenTicketsLoading: false });
    return chatsArray;
  },

  getInternalChats: async (user, setErrorMessage) => {
    set({ isInternalChatsLoading: true });
    const response = await getRequest(
      `${baseUrl}/chats/internalChats/${user?.id}`,
    );
    if (response.error) {
      setErrorMessage(
        'Erro ao buscar chats internos. '
        + 'Entre em contato com o suporte',
      );
      set({ isInternalChatsLoading: false });
      return;
    }
    const archived = response.filter(
      (chat) => chat.arquivado === 1,
    );
    set({
      internalChats: response,
      archivedInternalChats: archived || [],
      isInternalChatsLoading: false,
    });
  },

  updateInternalChatsWithNewMessage: async ({
    currentChatId,
    newMessage,
    setErrorMessage,
    userId,
  }) => {
    const {
      internalChats,
      archivedInternalChats,
    } = get();

    const isChatOpen = currentChatId === newMessage.chatId;
    let newMessageChat = internalChats.find((chat) => chat.id === newMessage.chatId);
    let nextArchivedInternalChats = archivedInternalChats;
    let shouldCloseArchivedSection = false;

    if (newMessageChat?.arquivado === 1) {
      newMessageChat = {
        ...newMessageChat,
        arquivado: 0,
      };

      nextArchivedInternalChats = archivedInternalChats
        .filter((archived) => archived.id !== newMessageChat.id);
      shouldCloseArchivedSection = true;
    }

    const updatedInternalChats = internalChats.map((chat) => {
      if (chat.id === newMessage.chatId) {
        return {
          ...(newMessageChat || chat),
          lastMessage: newMessage.body,
          createdAt: newMessage.createdAt,
          mediaType: newMessage.mediaType,
          mediaUrl: newMessage.mediaUrl,
          unreadCount: isChatOpen
            ? chat.unreadCount
            : (chat.unreadCount || 0) + 1,
        };
      }

      return chat;
    });

    if (!newMessageChat) {
      try {
        const response = await getRequest(
          `${baseUrl}/chats/getInternalChatById/${newMessage.chatId}/${userId}`,
        );

        if (response) {
          updatedInternalChats.push(response);
        }
      } catch (error) {
        setErrorMessage('Erro ao buscar novo chat');
      }
    }

    const sortedInternalChats = sortUserChatsByRecentMessages(updatedInternalChats);

    set({
      internalChats: sortedInternalChats,
      archivedInternalChats: nextArchivedInternalChats,
      openArchivedInternalChats: shouldCloseArchivedSection
        ? false
        : get().openArchivedInternalChats,
    });

    return sortedInternalChats;
  },
}));
